import { useEffect, useRef } from 'react'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { setPrompt } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { setGenerationAction } from 'Pods/ThreeDStudio/Store/actions/schema'
import { ACTION } from 'Pods/ThreeDStudio/Store/constants'

const MODE_PARAM = 'mode'
const PROMPT_PARAM = 'prompt'

// Generation actions a deep link is allowed to activate. Restricting to these two
// blocks a crafted URL from setting an arbitrary generationAction (e.g. an enhanced
// action that has no standalone UI).
const ALLOWED_MODES = [ACTION.TEXT_TO_3D, ACTION.IMAGE_TO_3D]

// Hydrates the generate panel from the home-search "AI mode" deep link
// (`/ai?mode=text-to-3d&prompt=...`). This page is served as shared, cached HTML by
// FastApplicationController and the SSR StaticRouter never sees the query string, so
// the params are read client-side after hydration (never baked into SSR props, which
// would leak one user's prompt across cache hits). The prompt input and mode tab are
// store-controlled, so writing the store is enough to populate the UI. After applying,
// the params are stripped from the URL so refresh/share/HMR don't re-apply them.
const useDeepLinkGeneration = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const appliedRef = useRef(false)

  const search = location?.search ?? ''

  useEffect(() => {
    if (appliedRef.current) return

    const params = new URLSearchParams(search)
    const mode = params.get(MODE_PARAM)
    const prompt = params.get(PROMPT_PARAM)

    // Both absent → nothing to apply. A prompt-only URL is still valid, so keep OR
    // semantics: bail only when there is neither an allowed mode nor a prompt.
    if (!ALLOWED_MODES.includes(mode) && !prompt) return

    if (ALLOWED_MODES.includes(mode)) {
      setGenerationAction(mode)
    }

    const trimmedPrompt = prompt?.trim()
    if (trimmedPrompt) {
      setPrompt(trimmedPrompt)
    }

    params.delete(MODE_PARAM)
    params.delete(PROMPT_PARAM)
    const nextSearch = params.toString()

    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true, state: location.state }
    )

    // Flip the guard only after every side effect has run, so a throwing setter can't
    // leave the deep link permanently un-applied for this mount.
    appliedRef.current = true
  }, [search, location.pathname, location.state, navigate])
}

export default useDeepLinkGeneration
